import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import * as XLSX from 'xlsx';

interface CsvRow {
  id: string;
  nom: string;
  prenom: string;
  numero_dossier: string;
}

export default function ImportPatientsCsvPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [previewData, setPreviewData] = useState<CsvRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ created: number; updated: number; skipped: number; errors: string[] } | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [progressTotal, setProgressTotal] = useState(0);
  const [progressDone, setProgressDone] = useState(0);
  const [lastBatchId, setLastBatchId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteResult, setDeleteResult] = useState<string | null>(null);
  const [batches, setBatches] = useState<{ batch_id: string; count: number; created_at: string }[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const [batchPatients, setBatchPatients] = useState<{ nom: string; prenom: string; numero_dossier: string }[]>([]);
  const [batchPatientsLoading, setBatchPatientsLoading] = useState(false);
  const [deleteTargetBatchId, setDeleteTargetBatchId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Au chargement, restaurer le batch_id depuis le localStorage et charger les batches
  useEffect(() => {
    const stored = localStorage.getItem('last_import_batch_id');
    if (stored) {
      setLastBatchId(stored);
    }
    loadBatches();
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4"></i>
          <div className="text-clinic-blue-dark text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!user || (role !== 'super_admin')) {
    navigate('/', { replace: true });
    return null;
  }

  const isHeaderRow = (values: string[]): boolean => {
    if (values.length === 0) return false;
    const firstCell = (values[0] || '').trim().toLowerCase();
    const headerKeywords = ['nom', 'name', 'prénom', 'prenom', 'first', 'last', 'id', 'numéro', 'numero', 'dossier', 'identification', 'n°', 'patient'];
    return headerKeywords.some((kw) => firstCell.includes(kw));
  };

  const cleanNumero = (raw: string): string => {
    let cleaned = raw.trim();
    // Enlever les décimales .0 si c'est un nombre Excel
    if (cleaned.match(/^\d+\.0+$/)) {
      cleaned = cleaned.replace(/\.0+$/, '');
    }
    return cleaned;
  };

  const parseXlsx = (data: Uint8Array): CsvRow[] => {
    const workbook = XLSX.read(data, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const jsonData = XLSX.utils.sheet_to_json<unknown[][]>(worksheet, { header: 1 });

    if (!jsonData || jsonData.length === 0) return [];

    // Détecter le format : tout en colonne A (séparé par virgules) ou multi-colonnes
    const firstRow = jsonData[0];
    const isSingleColumn = !firstRow || !Array.isArray(firstRow) || firstRow.length < 2;

    const rows: CsvRow[] = [];

    if (isSingleColumn) {
      // Format "tout en colonne A avec virgules"
      let startIndex = 0;

      // Vérifier si la première ligne ressemble à un en-tête
      if (jsonData.length > 0) {
        const firstCell = String((Array.isArray(firstRow) ? firstRow[0] : firstRow) ?? '').trim();
        // Tester sur le split par virgule
        const parts = firstCell.split(',').map((p: string) => p.trim().toLowerCase());
        if (isHeaderRow(parts)) {
          startIndex = 1;
        }
      }

      for (let i = startIndex; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row) continue;
        const cellValue = String((Array.isArray(row) ? row[0] : row) ?? '').trim();
        if (!cellValue) continue;

        // Splitter par virgule
        const parts = cellValue.split(',').map((p: string) => p.trim());
        const id = cleanNumero(parts[0] || '');
        const nom = (parts[1] || '').trim();
        const prenom = (parts[2] || '').trim();

        if (!id) continue;
        // Il faut au moins un nom ou un prénom pour identifier le patient
        if (!nom && !prenom) continue;

        const nom_final = nom || '*';
        const prenom_final = prenom || '*';

        const numero_dossier = `CSF${id}`;
        rows.push({ id: id, nom: nom_final, prenom: prenom_final, numero_dossier });
      }
    } else {
      // Format multi-colonnes classique
      let startIndex = 0;

      if (jsonData.length > 0) {
        const firstVals = (firstRow as unknown[]).map((c) => String(c ?? '').trim().toLowerCase());
        if (isHeaderRow(firstVals)) {
          startIndex = 1;
        }
      }

      for (let i = startIndex; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || !Array.isArray(row)) continue;

        const id = cleanNumero(String(row[0] ?? ''));
        const nom = String(row[1] ?? '').trim();
        const prenom = String(row[2] ?? '').trim();

        if (!id) continue;
        // Il faut au moins un nom ou un prénom pour identifier le patient
        if (!nom && !prenom) continue;

        const nom_final = nom || '*';
        const prenom_final = prenom || '*';

        const numero_dossier = `CSF${id}`;
        rows.push({ id: id, nom: nom_final, prenom: prenom_final, numero_dossier });
      }
    }

    return rows;
  };

  const parseCsv = (text: string): CsvRow[] => {
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    const rows: CsvRow[] = [];

    // Auto-détection : la première ligne est-elle un en-tête ?
    let startIndex = 0;
    if (lines.length > 0) {
      let parts: string[] = [];
      const firstLine = lines[0];
      if (firstLine.includes('\t')) {
        parts = firstLine.split('\t');
      } else if (firstLine.includes(';')) {
        parts = firstLine.split(';');
      } else {
        parts = firstLine.split(',');
      }
      const firstCell = (parts[0] || '').trim().toLowerCase();
      const headerKeywords = ['nom', 'name', 'prénom', 'prenom', 'first', 'last', 'id', 'numéro', 'numero', 'dossier', 'identification', 'n°', 'patient'];
      const looksLikeHeader = headerKeywords.some((kw) => firstCell.includes(kw));
      if (looksLikeHeader) {
        startIndex = 1;
      }
    }

    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i];
      // Detect delimiter: try tab first, then semicolon, then comma
      let parts: string[] = [];
      if (line.includes('\t')) {
        parts = line.split('\t');
      } else if (line.includes(';')) {
        parts = line.split(';');
      } else {
        parts = line.split(',');
      }

      const id = cleanNumero(parts[0] || '');
      const nom = (parts[1] || '').trim();
      const prenom = (parts[2] || '').trim();

      // Skip rows without ID
      if (!id) continue;
      // Il faut au moins un nom ou un prénom pour identifier le patient
      if (!nom && !prenom) continue;

      const nom_final = nom || '*';
      const prenom_final = prenom || '*';

      // Prepend CSF to the original ID
      const numero_dossier = `CSF${id}`;

      rows.push({ id: id, nom: nom_final, prenom: prenom_final, numero_dossier });
    }

    return rows;
  };

  const isXlsxFile = (name: string): boolean => {
    const lower = name.toLowerCase();
    return lower.endsWith('.xlsx') || lower.endsWith('.xls');
  };

  const isCsvFile = (name: string): boolean => {
    const lower = name.toLowerCase();
    return lower.endsWith('.csv') || lower.endsWith('.txt');
  };

  const handleFile = (file: File) => {
    setFileName(file.name);
    setImportResult(null);
    setSuccessMsg('');
    setErrorMsg('');

    if (isXlsxFile(file.name)) {
      // Parsing XLSX
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const parsed = parseXlsx(data);
          setPreviewData(parsed);
          if (parsed.length === 0) {
            setErrorMsg('Aucune donnée valide trouvée dans le fichier. Une ligne doit contenir au moins un N° ID + un Nom ou un Prénom. Les champs vides seront remplacés par une astérisque (*).');
          }
        } catch {
          setErrorMsg('Erreur lors de la lecture du fichier Excel. Vérifiez le format.');
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      // Parsing CSV/TXT
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const parsed = parseCsv(text);
          setPreviewData(parsed);
          if (parsed.length === 0) {
            setErrorMsg('Aucune donnée valide trouvée dans le fichier. Une ligne doit contenir au moins un N° ID + un Nom ou un Prénom. Les champs vides seront remplacés par une astérisque (*).');
          }
        } catch {
          setErrorMsg('Erreur lors de la lecture du fichier. Vérifiez le format CSV.');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (isCsvFile(file.name) || isXlsxFile(file.name))) {
      handleFile(file);
    } else {
      setErrorMsg('Veuillez déposer un fichier CSV (.csv) ou Excel (.xlsx)');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleImport = async () => {
    if (previewData.length === 0) return;
    setImporting(true);
    setImportResult(null);
    setSuccessMsg('');
    setErrorMsg('');

    const CHUNK_SIZE = 200;
    const chunks: CsvRow[][] = [];
    for (let i = 0; i < previewData.length; i += CHUNK_SIZE) {
      chunks.push(previewData.slice(i, i + CHUNK_SIZE));
    }

    setProgressTotal(chunks.length);
    setProgressDone(0);

    const totalResult = { created: 0, updated: 0, skipped: 0, errors: [] as string[] };

    // Générer un seul batch_id pour toute l'importation
    const batchId = crypto.randomUUID();
    setLastBatchId(batchId);
    localStorage.setItem('last_import_batch_id', batchId);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];

        const resp = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/import-patients-bulk`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session?.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              patients: chunk.map((r) => ({
                nom: r.nom,
                prenom: r.prenom,
                numero_dossier: r.numero_dossier,
              })),
              batch_id: batchId,
            }),
          }
        );

        const result = await resp.json();

        if (result.success) {
          totalResult.created += result.created || 0;
          totalResult.updated += result.updated || 0;
          totalResult.skipped += result.skipped || 0;
          if (result.errors && result.errors.length > 0) {
            totalResult.errors.push(...result.errors);
          }
        } else {
          totalResult.errors.push(`Lot ${i + 1}/${chunks.length} : ${result.error || 'Erreur inconnue'}`);
        }

        setProgressDone(i + 1);
        setImportResult({ ...totalResult });
      }

      setSuccessMsg(
        `Import terminé : ${totalResult.created} créés, ${totalResult.updated} mis à jour, ${totalResult.skipped} ignorés (${chunks.length} lot${chunks.length > 1 ? 's' : ''})`
      );
      loadBatches();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Erreur lors de l\'import.');
    } finally {
      setImporting(false);
    }
  };

  const clearFile = () => {
    setFileName('');
    setPreviewData([]);
    setImportResult(null);
    setSuccessMsg('');
    setErrorMsg('');
    setProgressTotal(0);
    setProgressDone(0);
  };

  async function loadBatches() {
    setBatchesLoading(true);
    try {
      const { data, error } = await supabase
        .from('patients_temporaires')
        .select('import_batch_id, created_at')
        .order('created_at', { ascending: false });

      if (error) { setBatchesLoading(false); return; }

      // Grouper par batch_id
      const batchMap: Record<string, { count: number; created_at: string }> = {};
      for (const row of data) {
        const bid = row.import_batch_id;
        if (!batchMap[bid]) {
          batchMap[bid] = { count: 0, created_at: row.created_at };
        }
        batchMap[bid].count++;
      }

      const batchList = Object.entries(batchMap).map(([batch_id, info]) => ({
        batch_id,
        count: info.count,
        created_at: info.created_at,
      }));

      setBatches(batchList);
    } catch {
      // ignore
    } finally {
      setBatchesLoading(false);
    }
  }

  async function loadBatchPatients(batchId: string) {
    if (expandedBatchId === batchId) {
      setExpandedBatchId(null);
      setBatchPatients([]);
      return;
    }

    setExpandedBatchId(batchId);
    setBatchPatientsLoading(true);
    try {
      const { data, error } = await supabase
        .from('patients_temporaires')
        .select('nom, prenom, numero_dossier')
        .eq('import_batch_id', batchId)
        .order('numero_dossier', { ascending: true });

      if (error) { setBatchPatientsLoading(false); return; }
      setBatchPatients(data || []);
    } catch {
      // ignore
    } finally {
      setBatchPatientsLoading(false);
    }
  }

  const handleDeleteBatch = async (batchId: string) => {
    setDeleting(true);
    setDeleteResult(null);
    setDeleteConfirmOpen(false);
    setDeleteTargetBatchId(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/delete-import-batch`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ batch_id: batchId }),
        }
      );
      const result = await resp.json();

      if (result.success) {
        const msgParts = [result.message || `${result.deleted} patient(s) supprimés avec succès.`];
        if (result.warnings && result.warnings.length > 0) {
          msgParts.push(`(${result.warnings.length} avertissement(s))`);
        }
        setDeleteResult(msgParts.join(' '));
        // Nettoyer si c'était le dernier batch
        if (batchId === lastBatchId) {
          setLastBatchId(null);
          localStorage.removeItem('last_import_batch_id');
        }
        setImportResult(null);
        setSuccessMsg('');
        loadBatches();
      } else {
        setDeleteResult(`Erreur : ${result.error || 'Échec de la suppression'}`);
      }
    } catch (err: unknown) {
      setDeleteResult(err instanceof Error ? err.message : 'Erreur lors de la suppression.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteImport = async () => {
    if (!lastBatchId) return;
    await handleDeleteBatch(lastBatchId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Import CSV / Excel — Patients</h1>
              <p className="mt-2 text-gray-600">
                Importez les patients depuis un fichier CSV ou Excel. Colonne A = N° d'identification, Colonne B = Nom, Colonne C = Prénom.
              </p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors text-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Retour admin
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {errorMsg && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm">{errorMsg}</span>
          </div>
        )}
        {successMsg && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-2xl flex items-start gap-2">
            <i className="ri-check-line mt-0.5 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm">{successMsg}</span>
          </div>
        )}

        {/* Zone de dépôt */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all ${
              dragOver
                ? 'border-clinic-green bg-clinic-green/5'
                : 'border-gray-300 hover:border-clinic-blue/50 hover:bg-gray-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-text-line text-3xl text-clinic-blue"></i>
            </div>
            {fileName ? (
              <>
                <p className="text-lg font-semibold text-gray-900">{fileName}</p>
                <p className="text-sm text-gray-500 mt-1">{previewData.length} lignes détectées</p>
                <button
                  onClick={(e) => { e.stopPropagation(); clearFile(); }}
                  className="mt-3 text-sm text-red-500 hover:text-red-700 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line mr-1"></i>Supprimer
                </button>
              </>
            ) : (
              <>
                <p className="text-lg font-semibold text-gray-700 mb-1">
                  Glissez-déposez votre fichier ici
                </p>
                <p className="text-sm text-gray-500">
                  ou cliquez pour parcourir (.csv, .xlsx)
                </p>
              </>
            )}
          </div>

          {/* Format attendu */}
          {!fileName && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-2xl">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-blue-600 mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Formats acceptés :</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700">
                    <li><strong>CSV</strong> (.csv, .txt) : Colonne A = ID, Colonne B = Nom, Colonne C = Prénom</li>
                    <li><strong>Excel</strong> (.xlsx, .xls) : mêmes colonnes, ou tout en colonne A séparé par des virgules</li>
                    <li>Les autres colonnes sont ignorées</li>
                    <li>Si la première ligne contient des mots comme "Nom", "Prénom", etc., elle est automatiquement détectée comme en-tête et ignorée</li>
                    <li>Si le <strong>Nom</strong> ou le <strong>Prénom</strong> est vide, une astérisque (<code>*</code>) sera mise à la place — vous pourrez retrouver ces patients et compléter leurs informations plus tard</li>
                  </ul>
                  <p className="mt-2 text-blue-600">
                    Le numéro de dossier sera automatiquement formaté : <strong>CSF</strong> + N° d'identification
                    (ex: <code>001/Me</code> &rarr; <code>CSF001/Me</code>)
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Aperçu */}
        {previewData.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-bold text-clinic-blue-dark mb-3">
              Aperçu ({previewData.length} patients)
            </h3>
            <div className="max-h-80 overflow-x-auto overflow-y-auto border border-gray-200 rounded-xl">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">#</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">N° original</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">N° dossier (CSF)</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Nom</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Prénom</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {previewData.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-gray-500">{i + 1}</td>
                      <td className="px-4 py-2.5 font-mono text-gray-600">{row.id}</td>
                      <td className="px-4 py-2.5">
                        <span className="px-2 py-0.5 bg-clinic-blue/10 text-clinic-blue rounded-full text-xs font-medium font-mono">
                          {row.numero_dossier}
                        </span>
                      </td>
                      <td className={`px-4 py-2.5 font-medium ${row.nom === '*' ? 'text-amber-600 italic' : 'text-gray-900'}`}>{row.nom}</td>
                      <td className={`px-4 py-2.5 ${row.prenom === '*' ? 'text-amber-600 italic' : 'text-gray-700'}`}>{row.prenom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Barre de progression */}
            {importing && progressTotal > 1 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Progression : lot {progressDone}/{progressTotal}
                  </span>
                  <span className="text-sm text-gray-500">
                    {Math.round((progressDone / progressTotal) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-3 bg-clinic-blue rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${Math.round((progressDone / progressTotal) * 100)}%` }}
                  ></div>
                </div>
              </div>
            )}

            <button
              onClick={handleImport}
              disabled={importing}
              className="mt-6 w-full px-6 py-3.5 bg-clinic-blue-dark text-white rounded-xl hover:bg-clinic-blue transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              {importing ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Importation en cours...
                </>
              ) : (
                <>
                  <i className="ri-upload-cloud-2-line"></i>
                  Importer {previewData.length} patients
                </>
              )}
            </button>
          </div>
        )}

        {/* Résultat */}
        {importResult && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-bold text-clinic-blue-dark mb-4 text-lg">Résultat de l'import</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200">
                <p className="text-3xl font-bold text-green-600">{importResult.created}</p>
                <p className="text-sm text-gray-600 mt-1">Créés</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-3xl font-bold text-blue-600">{importResult.updated}</p>
                <p className="text-sm text-gray-600 mt-1">Mis à jour</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-200">
                <p className="text-3xl font-bold text-gray-600">{importResult.skipped}</p>
                <p className="text-sm text-gray-600 mt-1">Ignorés</p>
              </div>
            </div>
            {importResult.errors.length > 0 && (
              <div className="p-4 bg-red-50 rounded-xl border border-red-200 max-h-48 overflow-y-auto">
                <p className="text-sm font-semibold text-red-700 mb-2">Erreurs :</p>
                {importResult.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-600">{e}</p>
                ))}
              </div>
            )}

            {/* ---- Bouton supprimer le lot ---- */}
            {lastBatchId && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={deleting}
                  className="flex items-center gap-2 px-5 py-3 bg-red-50 text-red-600 border border-red-200 rounded-xl hover:bg-red-100 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {deleting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Suppression en cours...
                    </>
                  ) : (
                    <>
                      <i className="ri-delete-bin-line"></i>
                      Annuler cette importation
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Supprime tous les patients créés ou mis à jour lors de cette importation, ainsi que toutes leurs données associées.
                </p>
              </div>
            )}

            {/* Message de résultat de suppression */}
            {deleteResult && (
              <div className={`mt-4 p-4 rounded-xl border ${
                deleteResult.includes('Erreur')
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'bg-green-50 border-green-200 text-green-700'
              }`}>
                <div className="flex items-start gap-2">
                  <i className={`${deleteResult.includes('Erreur') ? 'ri-error-warning-line' : 'ri-check-line'} mt-0.5 w-5 h-5 flex items-center justify-center`}></i>
                  <span className="text-sm">{deleteResult}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Historique des imports */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-clinic-blue-dark text-lg flex items-center gap-2">
              <i className="ri-history-line text-clinic-blue"></i>
              Historique des imports
            </h3>
            <button
              onClick={loadBatches}
              disabled={batchesLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className={`ri-refresh-line ${batchesLoading ? 'animate-spin' : ''}`}></i>
              Actualiser
            </button>
          </div>

          {/* Message de résultat de suppression */}
          {deleteResult && (
            <div className={`mb-4 p-4 rounded-xl border ${
              deleteResult.includes('Erreur')
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-green-50 border-green-200 text-green-700'
            }`}>
              <div className="flex items-start gap-2">
                <i className={`${deleteResult.includes('Erreur') ? 'ri-error-warning-line' : 'ri-check-line'} mt-0.5 w-5 h-5 flex items-center justify-center`}></i>
                <span className="text-sm">{deleteResult}</span>
              </div>
            </div>
          )}

          {batchesLoading && batches.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <i className="ri-loader-4-line animate-spin text-2xl mb-2 block"></i>
              <span className="text-sm">Chargement...</span>
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <i className="ri-inbox-line text-3xl mb-2 block"></i>
              <span className="text-sm">Aucun import trouvé. Importez un fichier ci-dessus pour commencer.</span>
            </div>
          ) : (
            <div className="space-y-3">
              {batches.map((batch) => (
                <div key={batch.batch_id} className="border border-gray-200 rounded-xl overflow-hidden">
                  {/* En-tête du batch */}
                  <div className="flex items-center justify-between p-4 bg-gray-50/50">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-clinic-blue/10 rounded-xl flex items-center justify-center">
                        <i className="ri-file-list-3-line text-clinic-blue text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {batch.count.toLocaleString()} patient{batch.count > 1 ? 's' : ''}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(batch.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                        <p className="text-xs text-gray-400 font-mono mt-0.5 truncate max-w-[300px]">
                          ID: {batch.batch_id}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => loadBatchPatients(batch.batch_id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-clinic-blue hover:bg-clinic-blue/5 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className={`ri-${expandedBatchId === batch.batch_id ? 'arrow-up-s' : 'arrow-down-s'}-line`}></i>
                        {expandedBatchId === batch.batch_id ? 'Masquer' : 'Voir les patients'}
                      </button>
                      <button
                        onClick={() => { setDeleteTargetBatchId(batch.batch_id); setDeleteConfirmOpen(true); }}
                        disabled={deleting}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                      >
                        <i className="ri-delete-bin-line"></i>
                        Supprimer
                      </button>
                    </div>
                  </div>

                  {/* Liste des patients (expandée) */}
                  {expandedBatchId === batch.batch_id && (
                    <div className="border-t border-gray-200">
                      {batchPatientsLoading ? (
                        <div className="text-center py-6 text-gray-400">
                          <i className="ri-loader-4-line animate-spin text-xl mb-1 block"></i>
                          <span className="text-xs">Chargement des patients...</span>
                        </div>
                      ) : batchPatients.length === 0 ? (
                        <div className="text-center py-6 text-gray-400">
                          <span className="text-xs">Aucun patient dans ce lot.</span>
                        </div>
                      ) : (
                        <div className="max-h-80 overflow-x-auto overflow-y-auto">
                          <table className="w-full text-sm min-w-[420px]">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="text-left px-4 py-2.5 font-semibold text-gray-600 text-xs">N° dossier</th>
                                <th className="text-left px-4 py-2.5 font-semibold text-gray-600 text-xs">Nom</th>
                                <th className="text-left px-4 py-2.5 font-semibold text-gray-600 text-xs">Prénom</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {batchPatients.map((p, i) => (
                                <tr key={i} className="hover:bg-gray-50">
                                  <td className="px-4 py-2 text-gray-500 font-mono text-xs">{p.numero_dossier}</td>
                                  <td className={`px-4 py-2 font-medium text-xs ${p.nom === '*' ? 'text-amber-600 italic' : 'text-gray-900'}`}>{p.nom}</td>
                                  <td className={`px-4 py-2 text-xs ${p.prenom === '*' ? 'text-amber-600 italic' : 'text-gray-700'}`}>{p.prenom}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---- Modale de confirmation suppression ---- */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <i className="ri-alert-line text-red-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Confirmer la suppression</h3>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
              <p className="text-sm text-red-700 font-medium mb-2">
                Êtes-vous sûr de vouloir supprimer TOUS les patients de ce lot d'importation ?
              </p>
              <ul className="text-xs text-red-600 space-y-1 list-disc list-inside">
                <li>Cette action est <strong>irréversible</strong></li>
                <li>Tous les patients de ce lot seront supprimés</li>
                <li>Toutes les données associées seront effacées (rendez-vous, prescriptions, suivis, résultats, etc.)</li>
                <li>Les comptes utilisateurs liés seront également supprimés</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setDeleteConfirmOpen(false); setDeleteTargetBatchId(null); }}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDeleteBatch(deleteTargetBatchId || lastBatchId || '')}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Suppression...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>
                    Supprimer définitivement
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}